/** @jsx jsx */
import { jsx } from '@emotion/react';
import { createContext, FC, useState, useContext } from 'react';
import { CheckoutFlowWebResponse } from '@/api/types';
import { PaymentMethod, SurchargeFee } from '@/types/common';
import { LinkInfoData, ShopperData } from '@/pages/paybylinkV2/types';
import { CreateQuoteResponse } from '@/types/automaticCurrencyConversion';

type Store = {
  shopperData: ShopperData;
  setShopperData: (data: ShopperData) => void;
  automaticCurrencyConversionData: string[];
  setAutomaticCurrencyConversionData: (data: string[]) => void;
  targetCurrency: string;
  setTargetCurrency: (data: string) => void;
  quote: CreateQuoteResponse;
  setQuote: (data: CreateQuoteResponse) => void;
  blockedCurrencies: string[];
  setBlockedCurrencies: (data: string[]) => void;
  // v2
  checkoutFlowData: CheckoutFlowWebResponse;
  setCheckoutFlowData: (data: CheckoutFlowWebResponse) => void;
  surcharge: SurchargeFee;
  setSurcharge: (data: SurchargeFee) => void;
  linkInfoData: LinkInfoData;
  setLinkInfoData: (data: LinkInfoData) => void;
  currentPaymentMethod: PaymentMethod;
  setCurrentPaymentMethod: (data: PaymentMethod) => void;
  ipCountry: string;
  setIpCountry: (data: string) => void;
  targetAmount: number | null;
  setTargetAmount: (data: number) => void;
  disablePay: boolean;
  setDisablePay: (data: boolean) => void;
};

export const StoreContextV2 = createContext<Store>({
  shopperData: null,
  setShopperData: null,
  automaticCurrencyConversionData: null,
  setAutomaticCurrencyConversionData: null,
  targetCurrency: null,
  setTargetCurrency: null,
  quote: null,
  setQuote: null,
  blockedCurrencies: null,
  setBlockedCurrencies: null,
  // v2
  checkoutFlowData: null,
  setCheckoutFlowData: null,
  surcharge: null,
  setSurcharge: null,
  linkInfoData: null,
  setLinkInfoData: null,
  currentPaymentMethod: null,
  setCurrentPaymentMethod: null,
  ipCountry: null,
  setIpCountry: null,
  targetAmount: null,
  setTargetAmount: null,
  disablePay: true,
  setDisablePay: null,
});

export const StoreProviderV2: FC = ({ children }) => {
  const [shopperData, setShopperData] = useState<ShopperData | null>(null);
  const [automaticCurrencyConversionData, setAutomaticCurrencyConversionData] = useState<string[] | null>(null);
  const [targetCurrency, setTargetCurrency] = useState<string | null>(null);
  const [quote, setQuote] = useState<CreateQuoteResponse | null>(null);
  const [blockedCurrencies, setBlockedCurrencies] = useState<string[] | null>(null);
  // v2
  const [targetAmount, setTargetAmount] = useState<number | null>(null);
  const [surcharge, setSurcharge] = useState<SurchargeFee | null>(null);
  const [checkoutFlowData, setCheckoutFlowData] = useState<CheckoutFlowWebResponse | null>(null);
  const [linkInfoData, setLinkInfoData] = useState<LinkInfoData | null>(null);
  const [currentPaymentMethod, setCurrentPaymentMethod] = useState<PaymentMethod | undefined>(undefined);
  const [ipCountry, setIpCountry] = useState<string | null>(null);
  const [disablePay, setDisablePay] = useState<boolean>(true);

  return (
    <StoreContextV2.Provider
      value={{
        shopperData,
        setShopperData,
        automaticCurrencyConversionData,
        setAutomaticCurrencyConversionData,
        targetCurrency,
        setTargetCurrency,
        quote,
        setQuote,
        blockedCurrencies,
        setBlockedCurrencies,
        // v2
        targetAmount,
        setTargetAmount,
        checkoutFlowData,
        setCheckoutFlowData,
        surcharge,
        setSurcharge,
        linkInfoData,
        setLinkInfoData,
        currentPaymentMethod,
        setCurrentPaymentMethod,
        ipCountry,
        setIpCountry,
        disablePay,
        setDisablePay,
      }}
    >
      {children}
    </StoreContextV2.Provider>
  );
};

export const useStoreV2 = (): Store => useContext(StoreContextV2);
